import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Gift, Search } from 'lucide-react';
import { commerceAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Field, inputCls, Toggle, fmtDate } from './shared';

const EmployerPicker = ({ value, onPick, t }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const h = setTimeout(() => commerceAPI.searchEmployers(q).then((r) => setResults(r.data)).catch(() => {}), 300);
    return () => clearTimeout(h);
  }, [q]);
  if (value) {
    return (
      <div className="mt-1 flex items-center justify-between gap-3 rounded-md border border-emerald-300 bg-emerald-50 px-3 h-10 text-sm" data-testid="grant-employer-selected">
        <span className="truncate"><strong>{value.company_name || '—'}</strong> · {value.email}</span>
        <button type="button" className="text-xs text-slate-600 underline" onClick={() => onPick(null)} data-testid="grant-employer-clear">{t('adminCommerce.change')}</button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="absolute left-3 top-[18px] h-4 w-4 text-slate-400" />
      <input className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('adminCommerce.grants.searchPlaceholder')} data-testid="grant-employer-search" />
      {results.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-auto" data-testid="grant-employer-results">
          {results.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                className="w-full text-left px-3 py-3 sm:py-2 text-sm hover:bg-slate-50 active:bg-emerald-50"
                onPointerDown={(ev) => ev.preventDefault()}
                onClick={() => { onPick(e); setQ(''); setResults([]); }}
                data-testid={`grant-employer-option-${e.id}`}
              >
                <span className="font-medium">{e.company_name || '—'}</span> <span className="text-slate-500">· {e.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const GrantsTab = () => {
  const { t } = useTranslation();
  const [employer, setEmployer] = useState(null);
  const [f, setF] = useState({ credits: 1, featured_credits: 0, validity_days: 90, reason: '', notify: true });
  const [saving, setSaving] = useState(false);
  const [grants, setGrants] = useState([]);
  const load = () => commerceAPI.listGrants().then((r) => setGrants(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  const submit = async () => {
    setSaving(true);
    try {
      await commerceAPI.createGrant({ ...f, employer_id: employer.id });
      toast.success(t('adminCommerce.grants.done'));
      setEmployer(null); setF({ credits: 1, featured_credits: 0, validity_days: 90, reason: '', notify: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setSaving(false); }
  };
  return (
    <div data-testid="grants-tab">
      <p className="text-sm text-slate-600 max-w-xl mb-4">{t('adminCommerce.grants.intro')}</p>
      <Card className="p-5 mb-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="block text-sm sm:col-span-2">
            <span className="font-medium text-slate-700">{t('adminCommerce.employer')}</span>
            <EmployerPicker value={employer} onPick={setEmployer} t={t} />
          </div>
          <Field label={t('adminCommerce.grants.credits')}><input type="number" min="1" className={inputCls} value={f.credits} onChange={set('credits')} data-testid="grant-credits" /></Field>
          <Field label={t('adminCommerce.grants.featured')}><input type="number" min="0" className={inputCls} value={f.featured_credits} onChange={set('featured_credits')} data-testid="grant-featured" /></Field>
          <Field label={t('adminCommerce.grants.validity')}><input type="number" min="1" className={inputCls} value={f.validity_days} onChange={set('validity_days')} data-testid="grant-validity" /></Field>
          <div className="flex items-end"><Toggle checked={f.notify} onChange={(v) => setF({ ...f, notify: v })} label={t('adminCommerce.grants.notify')} testId="grant-notify-toggle" /></div>
          <Field label={t('adminCommerce.grants.reason')} hint={t('adminCommerce.grants.reasonHint')} className="sm:col-span-2"><input className={inputCls} value={f.reason} onChange={set('reason')} placeholder={t('adminCommerce.grants.reasonPlaceholder')} data-testid="grant-reason" /></Field>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={saving || !employer || f.reason.trim().length < 3} className="bg-emerald-600 hover:bg-emerald-700" data-testid="grant-submit-btn"><Gift className="h-4 w-4 mr-1" />{t('adminCommerce.grants.submit')}</Button>
        </div>
      </Card>
      <h3 className="font-semibold text-slate-900 mb-3">{t('adminCommerce.grants.history')}</h3>
      {grants.length === 0 && <Card className="p-6 text-center text-slate-500" data-testid="grants-empty">{t('adminCommerce.grants.empty')}</Card>}
      <div className="space-y-2">
        {grants.map((g) => (
          <Card key={g.id} className="p-3 text-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4" data-testid={`grant-row-${g.id}`}>
            <span className="text-slate-500 w-24 shrink-0">{fmtDate(g.created_at)}</span>
            <span className="flex-1 min-w-0"><strong>{g.employer_company || g.employer_email}</strong> <span className="text-slate-500">· {g.employer_email}</span><br /><span className="text-slate-600">{g.reason}</span></span>
            <span className="font-semibold text-emerald-700 whitespace-nowrap">+{g.credits} {t('adminCommerce.bonusCreditsShort')}{g.featured_credits ? ` (${g.featured_credits} ★)` : ''} · {g.validity_days}d</span>
          </Card>
        ))}
      </div>
    </div>
  );
};
